import { Form } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { FormLeavingListener } from '@user-management/components';
import {
  GetAppRoleAppPermissionsDocument,
  useGetAppRoleAppPermissionsLazyQuery,
  useUpdateAppRoleAppPermissionsMutation,
} from '@user-management/graphql';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { useRole } from '@user-management/role-management';
import { useParams } from '@user-management/router';
import { FormProvider, useForm } from 'react-hook-form';

import { APP_PERMISSION_MAP } from '../../constants';
import type { AppOptions, AppRolePermissionForm } from '../../types';
import { extractCategoryAndNameAndAction, mapFormToAppPermissionIds } from '../../utils';

interface PermissionListFormProviderProps {
  children: React.ReactNode;
}

enum UpdateAppRolePermissionErrorType {
  DOES_NOT_HAVE_PERMISSION_TO_UPDATE_APPROLEID = 'DOES_NOT_HAVE_PERMISSION_TO_UPDATE_APPROLEID',
}

const ERROR_MESSAGE: Record<UpdateAppRolePermissionErrorType, string> = {
  [UpdateAppRolePermissionErrorType.DOES_NOT_HAVE_PERMISSION_TO_UPDATE_APPROLEID]:
    'ไม่สามารถแก้ไขสิทธิ์ของตัวเองได้',
};

export default function PermissionListFormProvider(
  props: Readonly<PermissionListFormProviderProps>,
) {
  const { children } = props;
  const hasUpdateRolePermission = useHasPermission(APP_PERMISSIONS.SETTING.ROLE.UPDATE);
  const { isMyRole } = useRole();
  const { showSnackbar } = useSnackbar();
  const { appRoleId = '', app = '' } = useParams(
    '/roles-and-permissions/:appRoleId/permissions/:app',
  );

  const formContext = useForm<AppRolePermissionForm>({
    disabled: isMyRole || !hasUpdateRolePermission,
    defaultValues: {},
  });

  const [updateAppRoleAppPermissions] = useUpdateAppRoleAppPermissionsMutation({
    onCompleted: (res) => {
      if (res?.suUpdateAppRoleAppPermissions.success) {
        showSnackbar({ message: 'Update Successful', variant: 'success' });
      } else {
        let message =
          res.suUpdateAppRoleAppPermissions.message ?? 'Update Failed, Please try again later';
        if (
          message.includes(
            UpdateAppRolePermissionErrorType.DOES_NOT_HAVE_PERMISSION_TO_UPDATE_APPROLEID,
          )
        ) {
          message =
            ERROR_MESSAGE[
              UpdateAppRolePermissionErrorType.DOES_NOT_HAVE_PERMISSION_TO_UPDATE_APPROLEID
            ];
        }
        showSnackbar({ message: message, variant: 'error' });
      }
    },
    onError: (error) => {
      showSnackbar({ message: error.message, variant: 'error' });
    },
    refetchQueries: [{ query: GetAppRoleAppPermissionsDocument, variables: { appRoleId } }],
    awaitRefetchQueries: true,
  });

  const [getAppRoleAppPermissions] = useGetAppRoleAppPermissionsLazyQuery();

  const handleSaveChanges = async (data: AppRolePermissionForm) => {
    const appPermissionIds = mapFormToAppPermissionIds(data);

    const { data: getAppRoleAppPermissionsResult } = await getAppRoleAppPermissions({
      variables: { appRoleId },
    });

    const currentAppRolePermissionItems =
      getAppRoleAppPermissionsResult?.getAppRoleAppPermissions ?? [];

    const currentAppPermission = APP_PERMISSION_MAP[app as AppOptions] ?? [];

    const excludedCurrentAppRolePermissionIds = currentAppRolePermissionItems
      .filter((item) => {
        const { category, name } = extractCategoryAndNameAndAction(item.id);
        return !currentAppPermission.includes(`${category}.${name}`);
      })
      .map((item) => item.id);

    const permissionIdsToUpdate = [...excludedCurrentAppRolePermissionIds, ...appPermissionIds];

    await updateAppRoleAppPermissions({
      variables: {
        appPermissionIds: permissionIdsToUpdate,
        updateAppRoleAppPermissionsAppRoleId: appRoleId,
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSaveChanges)}>{children}</Form>
      <FormLeavingListener onSubmit={handleSaveChanges} />
    </FormProvider>
  );
}
